<template>
  <main class="user-template">
    <NavBar class="user-template__navbar">
      <IconButton
        slot="left"
        type="button"
        data-testid="navbarGoBack"
        aria-label="Botão voltar"
        icon-name="ic_arrow_left"
        @click="goBack"
      />
      <template #center>
        <span data-testid="navbarTitle" class="user-template__navbar-title">
          {{ title }}
        </span>
      </template>
    </NavBar>
    <div class="user-template__header">
      <slot name="header" />
    </div>
    <component
      :is="isForm ? 'form' : 'div'"
      class="user-template__container"
      @submit.prevent="formSubmit"
    >
      <div>
        <slot />
      </div>
      <Footer class="user-template__footer">
        <ButtonPrimary
          data-testid="buttonSubmit"
          :label="submitName"
          :type="isForm ? 'submit' : 'button'"
          :disabled="isDisabled"
          :is-loading="isProcessing"
          size="large"
          @click="buttonSubmit"
        />
      </Footer>
    </component>
    <Notification :group="notificationGroup" />
  </main>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';

import { getString } from '@/modules/common/helpers/resources';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { Notification } from '@/modules/common/components/Notification';
import {
  BaseIcon,
  ButtonPrimary,
  IconButton,
  Footer,
  NavBar
} from '@warrenbrasil/nebraska-web';
import scrollToElement from 'scroll-to-element';

@Component({
  components: {
    BaseIcon,
    ButtonPrimary,
    IconButton,
    Footer,
    NavBar,
    Notification
  }
})
export default class UserTemplate extends Vue {
  @Prop({ default: false, required: false }) private isDisabled!: boolean;
  @Prop({ default: true, required: false }) private isForm!: boolean;
  @Prop({ required: false, default: undefined }) private onBack?: Function;
  @Prop({ required: true, default: '' }) private title!: string;
  @Prop({ required: true, default: async () => undefined })
  private submit!: () => Promise<string | undefined>;

  @Prop({ default: 'Enviar', required: false }) private submitName!: string;

  public goBack() {
    if (!this.onBack) this.$router.push({ name: 'home' });
    else this.onBack();
  }

  public notificationError(
    error?: string | { description: string; message: string }
  ) {
    let text = 'Não foi possível prosseguir';
    if (typeof error === 'string') text = error;
    if (typeof error === 'object' && error.message) {
      text = getString(error.message) || error.description || error.message;
    }
    this.$notify({
      group: this.notificationGroup,
      text,
      type: NotificationTypes.error
    });
  }

  public notificationSuccess(text = 'Ação realizada com sucesso') {
    this.$notify({
      group: this.notificationGroup,
      text,
      type: NotificationTypes.success
    });
  }

  private NebraskaColors = NebraskaColors;
  private notificationGroup = 'user-template-notifications';
  private isProcessing = false;

  private buttonSubmit() {
    if (this.isForm) return null;
    return this.submit();
  }

  private async formSubmit() {
    if (!this.isForm) return null;
    if (this.isDisabled)
      return this.notificationError(
        'Verifique se o preenchimento dos campos está correto'
      );
    this.isProcessing = true;
    return this.submit()
      .then(successNotificationText =>
        this.notificationSuccess(successNotificationText)
      )
      .catch(error => this.notificationError(error))
      .finally(() => {
        this.isProcessing = false;
        scrollToElement(this.$el, { offset: 0, duration: 300 });
      });
  }
}
</script>

<style lang="less" scoped>
.user-template {
  background-color: @background-secondary;
  --navbar-height: 64px;
  --footer-height: 150px;

  @media @tabletDown {
    --footer-height: 88px;
  }

  &__container {
    max-width: 100%;
    min-height: calc(100vh - var(--navbar-height));
    width: 580px;
    margin: 0 auto;
    padding: @size-spacing-x350;
    padding-bottom: calc(@size-spacing-x350 + var(--footer-height));
    display: flex;
    align-items: center;
    > * {
      width: 100%;
    }
  }
  @media @tabletDown {
    &__footer {
      height: inherit;
    }

    &__footer-button,
    /deep/.footer__content {
      width: 100%;
      border-radius: 0;
    }

    button {
      border-radius: 0;
      width: 100%;
    }
  }
}
.user-template__footer {
  z-index: 1;
}
</style>
